import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Divider from "@material-ui/core/Divider";
import ButtonBase from "@material-ui/core/ButtonBase";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import listPageStyles from "../../styles/listPageStyles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";
import UserDeleteDialog from "./UserDeleteDialog";

// ---------------------------------------------------------------------------
// Cores de estado (claro/escuro), no mesmo padrão do Financeiro
// ---------------------------------------------------------------------------
const TONES = {
  green: { light: "#2e7d32", dark: "#81c784" },
  amber: { light: "#b26a00", dark: "#ffd54f" },
  blue: { light: "#1565c0", dark: "#64b5f6" },
  grey: { light: "#757575", dark: "#bdbdbd" },
};
const toneColor = (theme, tone) =>
  (TONES[tone] || TONES.grey)[theme.palette.type === "dark" ? "dark" : "light"];

const AVATAR_COLORS = ["#2D7BE8", "#7b61ff", "#2e9d5b", "#f57c00", "#d81b60", "#00897b", "#5c6bc0", "#8d6e63"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[[...name].reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const getInitials = (name) =>
  name
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "?";

const FILTERS = [
  { key: "all", label: "Todos", count: (s) => s.total },
  { key: "admin", label: "Admin", count: (s) => s.admins },
  { key: "user", label: "Atendente", count: (s) => s.total - s.admins },
  { key: "agenda", label: "Agenda", count: (s) => s.serviceProviders },
  { key: "semfila", label: "Sem fila", count: (s) => s.withoutQueue },
];

const EMPTY_SUMMARY = { total: 0, online: 0, admins: 0, serviceProviders: 0, withoutQueue: 0 };

// O usuário passa no filtro atual? (usado para eventos em tempo real)
const matchesFilter = (user, filter) => {
  switch (filter) {
    case "admin":
      return user.profile === "admin";
    case "user":
      return user.profile !== "admin";
    case "agenda":
      return Boolean(user.isServiceProvider);
    case "semfila":
      return !user.queues?.length;
    default:
      return true;
  }
};

const matchesSearch = (user, term) =>
  !term || `${user.name} ${user.email}`.toLowerCase().includes(term);

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR");

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_USERS": {
      const next = [...state];
      action.payload.forEach((user) => {
        const idx = next.findIndex((u) => u.id === user.id);
        if (idx !== -1) next[idx] = user;
        else next.push(user);
      });
      return next;
    }
    case "UPSERT_USER": {
      const user = action.payload;
      const idx = state.findIndex((u) => u.id === user.id);
      // O evento de edição não traz todos os campos (ex.: online): mescla.
      if (idx !== -1) {
        const next = [...state];
        next[idx] = { ...state[idx], ...user };
        return next;
      }
      return [...state, user].sort(byName);
    }
    case "REMOVE_USER":
      return state.filter((u) => u.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => {
  const soft = (tone) => ({
    color: toneColor(theme, tone),
    backgroundColor: alpha(toneColor(theme, tone), theme.palette.type === "dark" ? 0.18 : 0.12),
  });
  return {
    ...listPageStyles(theme),
    kpis: {
      color: theme.palette.text.primary,
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 14,
      marginBottom: theme.spacing(1.75),
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        overflowX: "auto",
        gap: theme.spacing(1),
        border: "none",
        backgroundColor: "transparent",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      },
    },
    kpi: {
      // mesmo alinhamento para os indicadores clicáveis (ButtonBase) e o fixo
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "flex-start",
      textAlign: "left",
      fontFamily: "inherit",
      lineHeight: 1.5,
      color: "inherit",
      padding: theme.spacing(1.75, 2.25),
      borderLeft: `1px solid ${theme.palette.divider}`,
      minWidth: 0,
      "&:first-child": { borderLeft: "none" },
      [theme.breakpoints.down("sm")]: {
        flex: "0 0 auto",
        minWidth: 116,
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        "&:first-child": { border: `1px solid ${theme.palette.divider}` },
      },
    },
    kpiClickable: {
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
    },
    kpiLabel: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    kpiValue: {
      fontSize: 20,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      [theme.breakpoints.down("sm")]: { fontSize: 15 },
    },
    kpiCaption: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    dot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
    chipGroup: {
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      [theme.breakpoints.down("sm")]: {
        flexWrap: "nowrap",
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "& > *": { flex: "0 0 auto" },
      },
    },
    filterChip: { fontWeight: 600, height: 30 },
    chipCount: { marginLeft: 6, opacity: 0.75, fontVariantNumeric: "tabular-nums" },
    userCell: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), minWidth: 0 },
    avatar: {
      position: "relative",
      width: 36,
      height: 36,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontWeight: 700,
      fontSize: 12.5,
      color: "#fff",
      flexShrink: 0,
    },
    presence: {
      position: "absolute",
      right: -1,
      bottom: -1,
      width: 11,
      height: 11,
      borderRadius: "50%",
      border: `2px solid ${theme.palette.background.paper}`,
    },
    userName: { fontWeight: 500, display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
    nameText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    userEmail: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    youTag: {
      fontSize: 10.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      padding: "0 5px",
      lineHeight: "16px",
      flexShrink: 0,
    },
    tags: { display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" },
    pill: { height: 22, fontSize: 12, fontWeight: 600, borderRadius: 999 },
    pillAdmin: soft("blue"),
    pillUser: soft("grey"),
    pillAmber: soft("amber"),
    pillGreen: soft("green"),
    queueChip: {
      height: 22,
      fontSize: 12,
      borderRadius: 999,
      backgroundColor: "transparent",
      border: `1px solid ${theme.palette.divider}`,
      "& .MuiChip-label": { paddingLeft: 6 },
    },
    more: { fontSize: 12, color: theme.palette.text.secondary, cursor: "default" },
    conn: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap" },
    statusOn: { color: toneColor(theme, "green"), fontWeight: 600, fontSize: 13 },
    statusAway: { color: toneColor(theme, "amber"), fontWeight: 600, fontSize: 13 },
    statusOff: { color: theme.palette.text.secondary, fontSize: 13 },
    mobileRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.25, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    mobileInfo: { flex: 1, minWidth: 0 },
    mobileTags: { display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" },
  };
});

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
const Users = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user: loggedInUser } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(null);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [searchParam, setSearchParam] = useState("");
  const [filter, setFilter] = useState("all");
  const [users, dispatch] = useReducer(reducer, []);

  const [modal, setModal] = useState({ open: false, userId: null, focusPassword: false });
  const [deletingUser, setDeletingUser] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, user: null });

  // Filtro e busca atuais, lidos pelo socket sem recriar a conexão.
  const viewRef = useRef({ filter, searchParam });
  viewRef.current = { filter, searchParam };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, filter]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber, filter: filter === "all" ? undefined : filter },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
        setCount(data.count);
        if (data.summary) setSummary(data.summary);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchParam, pageNumber, filter]);

  // Números do topo depois de criar/editar/excluir (a lista em si é
  // atualizada pelo evento do socket).
  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/users/", { params: { pageNumber: 1, searchParam: "" } });
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      // silencioso: os números voltam a bater no próximo carregamento
    }
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-user`, (data) => {
      const { filter: f, searchParam: term } = viewRef.current;
      if (data.action === "update" || data.action === "create") {
        if (matchesFilter(data.user, f) && matchesSearch(data.user, term)) {
          dispatch({ type: "UPSERT_USER", payload: data.user });
        } else {
          dispatch({ type: "REMOVE_USER", payload: data.user.id });
        }
      }
      if (data.action === "delete") {
        dispatch({ type: "REMOVE_USER", payload: +data.userId });
      }
      refreshSummary();
    });

    return () => socket.disconnect();
  }, [refreshSummary]);

  const openModal = (user, focusPassword = false) =>
    setModal({ open: true, userId: user ? user.id : null, focusPassword });

  const closeModal = () => setModal({ open: false, userId: null, focusPassword: false });

  const closeMenu = () => setMenu({ anchor: null, user: null });

  const handleDeleted = (user) => {
    toast.success("Usuário excluído.");
    dispatch({ type: "REMOVE_USER", payload: user.id });
    refreshSummary();
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const connectionOf = (user) => {
    if (!user.whatsappId) return null;
    return whatsApps.find((w) => w.id === user.whatsappId) || user.whatsapp || null;
  };

  // ── Peças ────────────────────────────────────────────────────────────────
  const renderAvatar = (user) => (
    <div className={classes.avatar} style={{ backgroundColor: avatarColor(user.name) }} aria-hidden="true">
      {getInitials(user.name)}
      <span
        className={classes.presence}
        style={{
          backgroundColor: user.online
            ? toneColor(theme, user.away ? "amber" : "green")
            : theme.palette.divider,
        }}
      />
    </div>
  );

  const renderName = (user) => (
    <div className={classes.userName}>
      <span className={classes.nameText}>{user.name}</span>
      {user.id === loggedInUser?.id && <span className={classes.youTag}>você</span>}
    </div>
  );

  const profilePill = (user) =>
    user.profile === "admin" ? (
      <Chip size="small" label="Admin" className={`${classes.pill} ${classes.pillAdmin}`} />
    ) : (
      <Chip size="small" label="Atendente" className={`${classes.pill} ${classes.pillUser}`} />
    );

  const agendaPill = (user) =>
    user.isServiceProvider && (
      <Tooltip title="Atende na agenda">
        <Chip
          size="small"
          icon={<EventOutlinedIcon style={{ fontSize: 14, color: "inherit" }} />}
          label="Agenda"
          className={`${classes.pill} ${classes.pillGreen}`}
        />
      </Tooltip>
    );

  const noQueuePill = (
    <Tooltip title="Sem fila, não recebe atendimentos">
      <Chip size="small" label="Sem fila" className={`${classes.pill} ${classes.pillAmber}`} />
    </Tooltip>
  );

  const renderQueues = (user, max = 2) => {
    const list = user.queues || [];
    if (!list.length) return noQueuePill;
    const rest = list.slice(max);
    return (
      <>
        {list.slice(0, max).map((q) => (
          <Chip
            key={q.id}
            size="small"
            className={classes.queueChip}
            icon={<span className={classes.dot} style={{ backgroundColor: q.color, marginLeft: 8 }} />}
            label={q.name}
          />
        ))}
        {rest.length > 0 && (
          <Tooltip title={rest.map((q) => q.name).join(", ")}>
            <span className={classes.more}>+{rest.length}</span>
          </Tooltip>
        )}
      </>
    );
  };

  const renderConnection = (user) => {
    const conn = connectionOf(user);
    if (!conn) return <span className={classes.muted} style={{ fontSize: 13 }}>Nenhuma</span>;
    const connected = conn.status === "CONNECTED";
    return (
      <Tooltip title={connected ? "Conectada" : "Desconectada"}>
        <span className={classes.conn}>
          <span
            className={classes.dot}
            style={{ backgroundColor: connected ? toneColor(theme, "green") : toneColor(theme, "grey") }}
          />
          {conn.name}
        </span>
      </Tooltip>
    );
  };

  const moreButton = (user) => (
    <Tooltip title="Mais ações">
      <IconButton
        size="small"
        className={classes.iconAction}
        aria-label={`Mais ações para ${user.name}`}
        aria-haspopup="true"
        onClick={(e) => setMenu({ anchor: e.currentTarget, user })}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const kpis = [
    { key: "all", label: "Usuários", value: summary.total, caption: "cadastrados", tone: "grey" },
    { key: null, label: "Online agora", value: summary.online, caption: "conectados no sistema", tone: "green" },
    { key: "admin", label: "Administradores", value: summary.admins, caption: "acesso total", tone: "blue" },
    {
      key: "semfila",
      label: "Sem fila",
      value: summary.withoutQueue,
      caption: "não recebem atendimentos",
      tone: "amber",
      highlight: summary.withoutQueue > 0,
    },
  ];

  const countLabel =
    summary.total > 0
      ? `${summary.total} usuário${summary.total === 1 ? "" : "s"} · ${summary.online} online agora`
      : null;

  const emptyState = (
    <div className={classes.emptyState}>
      <PeopleOutlineIcon className={classes.emptyIcon} />
      <Typography variant="body2">
        {searchParam
          ? `Nenhum usuário encontrado para "${searchParam}".`
          : filter !== "all"
          ? "Nenhum usuário neste filtro."
          : "Nenhum usuário cadastrado ainda."}
      </Typography>
      {!searchParam && filter === "all" && (
        <Button size="small" color="primary" variant="outlined" onClick={() => openModal(null)}>
          Adicionar primeiro usuário
        </Button>
      )}
    </div>
  );

  return (
    <MainContainer>
      <UserModal
        open={modal.open}
        onClose={closeModal}
        userId={modal.userId}
        focusPassword={modal.focusPassword}
      />
      <UserDeleteDialog
        user={deletingUser}
        open={Boolean(deletingUser)}
        onClose={() => setDeletingUser(null)}
        onDeleted={handleDeleted}
      />

      <Menu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        <MenuItem
          onClick={() => {
            openModal(menu.user);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            openModal(menu.user, true);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <VpnKeyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Trocar senha
        </MenuItem>
        <Divider />
        <MenuItem
          style={{ color: theme.palette.error.main }}
          onClick={() => {
            setDeletingUser(menu.user);
            closeMenu();
          }}
        >
          <ListItemIcon style={{ color: "inherit" }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          Excluir
        </MenuItem>
      </Menu>

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Usuários</Title>
          {countLabel && (
            <Typography variant="body2" className={classes.muted}>
              {countLabel}
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={() => openModal(null)}
          >
            Novo usuário
          </Button>
        </div>
      </div>

      <div className={classes.kpis}>
        {kpis.map((k) => {
          const content = (
            <>
              <div className={classes.kpiLabel}>
                <span className={classes.dot} style={{ backgroundColor: toneColor(theme, k.tone) }} />
                {k.label}
              </div>
              <div
                className={classes.kpiValue}
                style={k.highlight ? { color: toneColor(theme, k.tone) } : undefined}
              >
                {count === null ? "—" : k.value}
              </div>
              <div className={classes.kpiCaption}>{k.caption}</div>
            </>
          );
          return k.key ? (
            <ButtonBase
              key={k.label}
              className={`${classes.kpi} ${classes.kpiClickable}`}
              onClick={() => setFilter(k.key)}
              aria-label={`Filtrar: ${k.label}`}
            >
              {content}
            </ButtonBase>
          ) : (
            <div key={k.label} className={classes.kpi}>
              {content}
            </div>
          );
        })}
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nome ou e-mail"
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
            inputProps={{ "aria-label": "Buscar usuários" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.chipGroup} role="group" aria-label="Filtro">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <Chip
                  key={f.key}
                  clickable
                  className={classes.filterChip}
                  color={active ? "primary" : "default"}
                  variant={active ? "default" : "outlined"}
                  aria-pressed={active}
                  onClick={() => setFilter(f.key)}
                  label={
                    <>
                      {f.label}
                      <span className={classes.chipCount}>{f.count(summary)}</span>
                    </>
                  }
                />
              );
            })}
          </div>
        </div>

        <div className={classes.tableWrapper} onScroll={handleScroll}>
          {!loading && users.length === 0 && emptyState}

          {isMobile ? (
            <div>
              {users.map((user) => (
                <div key={user.id} className={classes.mobileRow}>
                  {renderAvatar(user)}
                  <div className={classes.mobileInfo}>
                    {renderName(user)}
                    <div className={classes.userEmail}>{user.email}</div>
                    <div className={classes.mobileTags}>
                      {profilePill(user)}
                      {user.queues?.length ? (
                        <Chip
                          size="small"
                          label={`${user.queues.length} ${user.queues.length === 1 ? "fila" : "filas"}`}
                          className={`${classes.pill} ${classes.pillUser}`}
                        />
                      ) : (
                        noQueuePill
                      )}
                      {agendaPill(user)}
                    </div>
                  </div>
                  {moreButton(user)}
                </div>
              ))}
              {loading && (
                <Typography align="center" className={classes.muted} style={{ padding: 24 }}>
                  Carregando...
                </Typography>
              )}
            </div>
          ) : (
            (users.length > 0 || loading) && (
              <Table stickyHeader size="small" style={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>
                      Usuário
                    </TableCell>
                    <TableCell className={classes.headCell}>Perfil</TableCell>
                    <TableCell className={classes.headCell}>Filas</TableCell>
                    <TableCell className={classes.headCell}>Conexão padrão</TableCell>
                    <TableCell className={classes.headCell}>Status</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20, width: 100 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className={classes.row}>
                      <TableCell style={{ paddingLeft: 20, maxWidth: 320 }}>
                        <div className={classes.userCell}>
                          {renderAvatar(user)}
                          <div style={{ minWidth: 0 }}>
                            {renderName(user)}
                            <div className={classes.userEmail}>{user.email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={classes.tags}>
                          {profilePill(user)}
                          {agendaPill(user)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={classes.tags}>{renderQueues(user)}</div>
                      </TableCell>
                      <TableCell>{renderConnection(user)}</TableCell>
                      <TableCell>
                        {user.online && user.away ? (
                          <span className={classes.statusAway}>Ausente</span>
                        ) : user.online ? (
                          <span className={classes.statusOn}>Online</span>
                        ) : (
                          <span className={classes.statusOff}>Offline</span>
                        )}
                      </TableCell>
                      <TableCell style={{ paddingRight: 20 }}>
                        <div className={classes.actions}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              className={classes.iconAction}
                              aria-label={`Editar ${user.name}`}
                              onClick={() => openModal(user)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {moreButton(user)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={6} />}
                </TableBody>
              </Table>
            )
          )}
        </div>

        {users.length > 0 && (
          <div className={classes.footer}>
            {`Mostrando ${users.length}${count !== null ? ` de ${count}` : ""}`}
            {!loading && !hasMore ? " · ordenado por nome" : ""}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Users;
